import { defineStore } from "pinia";
import ApiApps from "@/apis/ApiApps";
import ConduitApiUsers from "@/apis/ConduitApiUsers";
import { ref } from "vue";
import { catchable } from "./piniaUtils";

export const useEndUserStore = defineStore("end-user", () => {
  const actions = ref({});
  const results = ref([]);
  const traits = ref({});

  const search = ({ appId, query }) =>
    catchable({
      t: async () => (results.value = await ApiApps.searchEndUsers(appId, query)),
      loadable: true,
    });

  const remove = ({ appId, endUserId }) =>
    catchable({
      t: () => ApiApps.deleteEndUser(appId, endUserId),
      loadable: true,
    });

  const create = ({ appId, traits }) =>
    catchable({
      t: () => ApiApps.createEndUser(appId, traits),
      loadable: true,
    });

  const getActions = (
    { appId, userId, params, waitForTraits = false, returnValue = false } = {
      waitForTraits: false,
      returnValue: false,
    },
  ) =>
    catchable({
      t: async () => {
        let actionsIn = await ConduitApiUsers.ping(appId, userId, params);
        let count = 0;

        // Only retry while a non-null payload came back but isn't ready yet;
        // a null payload means a hard failure (ping returns null on non-OK), so
        // stop immediately in both branches instead of polling a dead request.
        const condition = () =>
          !!actionsIn &&
          (waitForTraits ? !actionsIn.traits : !!actionsIn.paths && !actionsIn.paths.length);

        while (condition() && count < 3) {
          actionsIn = await ConduitApiUsers.ping(appId, userId, params);
          await new Promise(res => setTimeout(res, 3000));
          count++;
        }
        if (returnValue) return actionsIn;
        return (actions.value = actionsIn);
      },
      loadable: true,
    });

  const getUserTraits = ({ appId, userId }) =>
    catchable({
      t: async () => {
        const traitsData = await ApiApps.showEndUser(appId, userId);
        return (traits.value = traitsData);
      },
      loadable: true,
    });

  return { actions, results, traits, search, remove, create, getActions, getUserTraits };
});
